import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, Mail, Lock, Building2, Eye, EyeOff } from 'lucide-react';
import useAuthStore from '../../store/authStore';
import { Button, Input, Alert } from '../../components/common';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, isLoading, error, clearError, isAuthenticated } = useAuthStore();

  const [formData, setFormData] = useState({
    nombre_completo: '',
    email: '',
    password: '',
    confirm_password: '',
    nombre_organizacion: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');

  // Redirigir si ya está autenticado
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Limpiar error al desmontar
  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setLocalError('');
    clearError();
  };

  const validateForm = () => {
    if (!formData.nombre_completo || !formData.email || !formData.password || !formData.confirm_password) {
      setLocalError('Por favor complete todos los campos obligatorios');
      return false;
    }

    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setLocalError('Por favor ingrese un email válido');
      return false;
    }

    // Validar contraseña
    if (formData.password.length < 8) {
      setLocalError('La contraseña debe tener al menos 8 caracteres');
      return false;
    }

    // Validar coincidencia de contraseñas
    if (formData.password !== formData.confirm_password) {
      setLocalError('Las contraseñas no coinciden');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (!validateForm()) {
      return;
    }

    // Preparar datos para el registro
    const registerData = {
      nombre_completo: formData.nombre_completo,
      email: formData.email,
      password: formData.password,
      nombre_organizacion: formData.nombre_organizacion || undefined,
    };

    // Intentar registro
    const result = await register(registerData);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setLocalError(result.error || 'Error al registrarse');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-primary-600 rounded-full flex items-center justify-center">
            <span className="text-3xl font-bold text-white">M</span>
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Crear cuenta
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Únete a Mentor de Ética Digital
          </p>
        </div>

        {/* Form */}
        <div className="bg-white shadow-xl rounded-lg p-8">
          {(localError || error) && (
            <Alert
              type="error"
              message={localError || error}
              onClose={() => {
                setLocalError('');
                clearError();
              }}
              className="mb-6"
            />
          )}

          <form className="space-y-5" onSubmit={handleSubmit}>
            <Input
              label="Nombre completo"
              name="nombre_completo"
              type="text"
              placeholder="Juan Pérez"
              value={formData.nombre_completo}
              onChange={handleChange}
              icon={User}
              required
              disabled={isLoading}
            />

            <Input
              label="Correo electrónico"
              name="email"
              type="email"
              placeholder="tu@email.com"
              value={formData.email}
              onChange={handleChange}
              icon={Mail}
              required
              disabled={isLoading}
            />

            <Input
              label="Organización (opcional)"
              name="nombre_organizacion"
              type="text"
              placeholder="Mi Empresa"
              value={formData.nombre_organizacion}
              onChange={handleChange}
              icon={Building2}
              disabled={isLoading}
            />

            <div className="relative">
              <Input
                label="Contraseña"
                name="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Mínimo 8 caracteres"
                value={formData.password}
                onChange={handleChange}
                icon={Lock}
                required
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>

            <Input
              label="Confirmar contraseña"
              name="confirm_password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Repite tu contraseña"
              value={formData.confirm_password}
              onChange={handleChange}
              icon={Lock}
              required
              disabled={isLoading}
            />

            <div className="flex items-center">
              <input
                id="terms"
                name="terms"
                type="checkbox"
                required
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="terms" className="ml-2 block text-sm text-gray-900">
                Acepto los{' '}
                <a href="#" className="text-primary-600 hover:text-primary-500">
                  términos y condiciones
                </a>
              </label>
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={isLoading}
              className="w-full"
            >
              Crear cuenta
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  ¿Ya tienes una cuenta?
                </span>
              </div>
            </div>

            <div className="mt-6">
              <Link
                to="/login"
                className="w-full flex justify-center py-2 px-4 border border-primary-600 rounded-md shadow-sm text-sm font-medium text-primary-600 bg-white hover:bg-primary-50 transition-colors duration-200"
              >
                Iniciar Sesión
              </Link>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-sm text-gray-600">
          © 2025 Mentor de Ética Digital. Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
